import RefreshIcon from "@mui/icons-material/Refresh"
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import {
  ReservationSyncDto,
  useGetReservationSyncsQuery,
  type VerdictDto,
} from "src/features/things/reservationSyncApi"
import SaLayout from "./SaLayout"
import {
  fmtCount,
  fmtLocalDateTime,
  fmtNum,
  RunStatusChip,
  VerdictChip,
  VersionBadge,
} from "./reservationSync/ReservationSyncChrome"
import { SOURCE_ORDER } from "./reservationSync/SourceCards"

/**
 * Every stored reservation-drift run (LOCKER-374 D21, LOCKER-421 `DoD8`).
 *
 * READ-ONLY: this page has no action that writes, and neither does the API behind
 * it — a sweep, a sync and a revert are all operator-run from a shell.
 *
 * ## Grouped by tenant, and the group header carries nothing
 *
 * The tenant header is a GROUP LABEL, not a row. It shows how many lockers are
 * under it and how many of those have actionable drift — both counts of rows in
 * this table — and it carries **no verdict and no total**, because nothing sums
 * across lockers: an inconclusive run next to a clean one would make any tenant
 * figure a guess. There is also no tenant route to click through to (`OOS10`).
 */
const ALL = "__all__"

export const SaReservationSyncsPage: FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data, isLoading, isFetching, error, refetch } =
    useGetReservationSyncsQuery({ limit: 2000 })
  const [version, setVersion] = useState<"all" | "v1" | "v2">("all")
  const [tenant, setTenant] = useState<string>(searchParams.get("tenantId") ?? ALL)
  const [verdict, setVerdict] = useState<VerdictDto | typeof ALL>(ALL)

  const all = useMemo(() => data?.things ?? [], [data])

  const tenants = useMemo(() => {
    const byId = new Map<string, string>()
    for (const run of all) byId.set(run.tenantId, run.tenantName ?? run.tenantId)
    return [...byId.entries()].sort((a, b) => (a[1] < b[1] ? -1 : 1))
  }, [all])

  const runs = useMemo(
    () =>
      [...all]
        .filter((r) => version === "all" || r.sstApiVersion === version)
        .filter((r) => tenant === ALL || r.tenantId === tenant)
        .filter((r) => verdict === ALL || r.verdict === verdict)
        .sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1)),
    [all, version, tenant, verdict],
  )

  /** Runs bucketed by tenant, tenants ordered by name, runs newest-first within. */
  const groups = useMemo(() => {
    const byTenant = new Map<string, ReservationSyncDto[]>()
    for (const run of runs) {
      const list = byTenant.get(run.tenantId)
      if (list) list.push(run)
      else byTenant.set(run.tenantId, [run])
    }
    return [...byTenant.entries()].sort((a, b) => {
      const nameA = a[1][0].tenantName ?? a[0]
      const nameB = b[1][0].tenantName ?? b[0]
      return nameA < nameB ? -1 : 1
    })
  }, [runs])

  const v1 = all.filter((r) => r.sstApiVersion === "v1").length
  const v2 = all.length - v1

  return (
    <SaLayout selectedMenuItemLabelKey="reservationSyncs">
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 3, md: 5 }, py: { xs: 4, md: 5 } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontSize: "1.3rem", fontWeight: 600 }}>
            Reservation sync runs
          </Typography>
          {/*
            Runs arrive from a shell, not from this page, so there is no mutation
            here to invalidate the cache — an operator watching a sweep land has no
            other way to see it than reloading the whole app.
          */}
          <Tooltip title="Re-read the stored runs">
            <IconButton
              size="small"
              aria-label="refresh the run list"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              {isFetching ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: "80ch", mb: 3 }}>
          One record per locker per run of the locally-operated drift tooling. Grouped
          by tenant for navigation — a tenant is not itself assessed.
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              PARTNER API
            </Typography>
            {(["all", "v1", "v2"] as const).map((v) => (
              <Chip
                key={v}
                size="small"
                label={
                  v === "all"
                    ? `all (${fmtNum(all.length)})`
                    : `${v} (${fmtNum(v === "v1" ? v1 : v2)})`
                }
                onClick={() => setVersion(v)}
                variant={version === v ? "filled" : "outlined"}
              />
            ))}
          </Stack>

          <TextField
            select
            size="small"
            label="Tenant"
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value={ALL}>All tenants</MenuItem>
            {tenants.map(([id, name]) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Verdict"
            value={verdict}
            onChange={(e) => setVerdict(e.target.value as VerdictDto | typeof ALL)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value={ALL}>Any</MenuItem>
            <MenuItem value="ACTIONABLE">Actionable</MenuItem>
            <MenuItem value="QUALIFIED">Qualified</MenuItem>
            <MenuItem value="INCONCLUSIVE">Inconclusive</MenuItem>
          </TextField>
        </Stack>

        {isLoading && <Typography variant="body2">loading runs…</Typography>}
        {!!error && (
          <Typography variant="body2" sx={{ color: "error.main" }}>
            could not load the runs
          </Typography>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Locker</TableCell>
              <TableCell>API</TableCell>
              <TableCell>Verdict</TableCell>
              {SOURCE_ORDER.map((s) => (
                <TableCell key={s} align="right">
                  {s.toUpperCase()}
                </TableCell>
              ))}
              <TableCell align="right">Actionable</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Run at</TableCell>
              <TableCell>Run</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map(([tenantId, tenantRuns]) => (
              <TenantGroup key={tenantId} tenantId={tenantId} runs={tenantRuns} navigate={navigate} />
            ))}
            {!isLoading && !runs.length && (
              <TableRow>
                <TableCell colSpan={11} sx={{ textAlign: "center", py: 5 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No runs match. They appear here as soon as an operator runs
                    sweepReservationDrift.ts against this environment.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}
        >
          <Typography variant="caption" sx={{ flexGrow: 1, lineHeight: 1.55 }}>
            <strong>The tenant header is a group label, not a row.</strong> It carries
            no verdict and no total, because nothing sums across lockers: an
            inconclusive run next to a clean one would make any tenant figure a guess.
            The counts it does show — how many lockers, how many with actionable
            drift — are counts of rows in this table.
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontFamily: "monospace", color: "text.disabled", whiteSpace: "nowrap" }}
          >
            {runs.length} run{runs.length === 1 ? "" : "s"} · {groups.length} tenant
            {groups.length === 1 ? "" : "s"}
          </Typography>
        </Stack>
      </Box>
    </SaLayout>
  )
}

const TenantGroup: FC<{
  tenantId: string
  runs: ReservationSyncDto[]
  navigate: ReturnType<typeof useNavigate>
}> = ({ tenantId, runs, navigate }) => {
  const lockerIds = new Set(runs.map((r) => r.lockerId))
  const withActionableDrift = runs.filter(
    (r) => r.driftTotal !== null && r.driftTotal > 0,
  ).length
  const inconclusive = runs.filter((r) => r.verdict === "INCONCLUSIVE").length

  return (
    <>
      <TableRow>
        <TableCell colSpan={11} sx={{ bgcolor: "action.hover" }}>
          <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
            <Chip
              size="small"
              label="TENANT"
              sx={{ fontWeight: 700, letterSpacing: "0.07em", height: 20 }}
            />
            <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
              {runs[0].tenantName ?? tenantId}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: "monospace", color: "text.disabled" }}
            >
              {tenantId}
            </Typography>
            <Box sx={{ color: "divider" }}>|</Box>
            {/* Counts of ROWS IN THIS TABLE. Never a verdict, never a drift total. */}
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {lockerIds.size} locker{lockerIds.size === 1 ? "" : "s"} ·{" "}
              {withActionableDrift} with actionable drift
              {inconclusive ? ` · ${inconclusive} inconclusive` : ""}
            </Typography>
          </Stack>
        </TableCell>
      </TableRow>
      {runs.map((r) => (
        <TableRow
          key={r.id}
          hover
          sx={{ cursor: "pointer" }}
          onClick={() =>
            navigate(
              `/sa/reservation-syncs/${encodeURIComponent(r.runId)}?tenantId=${encodeURIComponent(r.tenantId)}`,
            )
          }
        >
          <TableCell>
            <Typography sx={{ fontWeight: 600, fontSize: "0.82rem" }}>
              {r.lockerName ?? r.sstLockerId}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.disabled", fontFamily: "monospace" }}
            >
              {r.sstLockerId}
            </Typography>
          </TableCell>
          <TableCell>
            <VersionBadge version={r.sstApiVersion} />
          </TableCell>
          <TableCell>
            <VerdictChip verdict={r.verdict} />
          </TableCell>
          {SOURCE_ORDER.map((s) => (
            <TableCell key={s} align="right" sx={{ fontFamily: "monospace" }}>
              {/* An em dash for a source that was not read, never a zero. */}
              {fmtCount(r.sources?.[s]?.fetched)}
            </TableCell>
          ))}
          <TableCell
            align="right"
            sx={{
              fontFamily: "monospace",
              fontWeight: 600,
              color:
                r.driftTotal === null
                  ? "text.disabled"
                  : r.driftTotal > 0
                    ? "error.main"
                    : "text.secondary",
            }}
          >
            {fmtCount(r.driftTotal)}
          </TableCell>
          <TableCell>
            <RunStatusChip status={r.status} />
          </TableCell>
          {/* The run's start in the viewer's own timezone (LOCKER-437). */}
          <TableCell
            title={new Date(r.startedAt).toLocaleString()}
            sx={{ fontSize: "0.72rem", fontFamily: "monospace", whiteSpace: "nowrap" }}
          >
            {fmtLocalDateTime(r.startedAt)}
          </TableCell>
          <TableCell sx={{ fontSize: "0.72rem", fontFamily: "monospace" }}>
            {r.startedAt.replace("T", " ").slice(11, 19)}Z
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}

export default SaReservationSyncsPage
